import { computed, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { finalize, map, shareReplay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { Page } from '@frontend/core';
import { LanguageService } from '@frontend/common';

/** `{ category: { type: pages } }` — pages are always stored in an array. */
type PagesByCategoryAndType = {
  [category: string]: { [type: string]: Page[] };
};

@Injectable({
  providedIn: 'root'
})
export class PageService {

  // Locale-keyed cache of translated pages: one `category → type → pages` tree per
  // UI language, held in parallel and never flushed on locale change, so switching
  // back to a previously-viewed language is instant and free. Only imperative
  // consumers read this cache (via getPagesByCategoryAndType); those that must
  // refresh on locale change do so themselves (e.g. reloadOnLocaleChange), so the
  // service needs no locale subscription of its own.
  private _pagesByLocale = signal<Record<string, PagesByCategoryAndType>>({});

  /** Cached pages for the currently-active UI language (empty until fetched). */
  readonly activePages = computed<PagesByCategoryAndType>(
    () => this._pagesByLocale()[this.activeLanguageKey] ?? {},
  );

  // In-flight requests keyed `locale::category::type`, so two consumers asking for
  // the same pages at the same time share a single HTTP call.
  private activePageRequests: { [key: string]: Observable<Page[]> } = {};

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
  ) { }

  /** Cached pages for the currently-active UI language. */
  get pagesByCategoryAndType(): PagesByCategoryAndType {
    return this.activePages();
  }

  private get activeLanguageKey(): string {
    return this.languageService.activeLanguage().languageKey;
  }

  /**
   * Logout / personal-data contract (reserved). Pages are public content, not
   * personal data, so this is intentionally a no-op — never repurpose it for locale
   * handling.
   */
  clearData() {
    // no-op
  }

  transformPage (page) : Page {
    page.created_at = page.created_at ? new Date(page.created_at) : null;
    page.updated_at = page.updated_at ? new Date(page.updated_at) : null;
    // p.published_at = p.published_at ? new Date(p.published_at) : null;
    let transformedPage : Page = page;
    return transformedPage;
  };
  transformPages (pages) : Page[] {
    let transformedPages : Page[] = [];
    pages.forEach(p=>{
      transformedPages.push(this.transformPage(p));
    });
    return transformedPages;
  }

  cachePages (pages : Page[], category : string,  type : string){
    if (!pages || !category){return;};
    const key = this.activeLanguageKey;
    this._pagesByLocale.update((byLocale) => {
      const localePages = byLocale[key] ?? {};
      return {
        ...byLocale,
        [key]: {
          ...localePages,
          [category]: { ...(localePages[category] ?? {}), [type]: pages },
        },
      };
    });
    return pages;
  };

  private getCachedPages (category : string, type : string) : Page[] | undefined {
    return this.activePages()[category]?.[type];
  }

  getPagesByCategoryAndType (category: string, type : string) : Observable<Page[]> {
    if (!category) { throw 'category is required';};

    const cachedPages = this.getCachedPages(category, type);
    if (cachedPages){
      return of(cachedPages);
    };

    const requestKey = `${this.activeLanguageKey}::${category}::${type}`;
    if (this.activePageRequests[requestKey]){
      return this.activePageRequests[requestKey];
    };

    let url = 'api/v1/pages?category='+category+'&type=';
    if (type) {
      url+=type;
    };
    const request$ = this.http.get<{'data': Page[]}>(url)
        .pipe(
          map(response =>{
            if (response && response.data ){
              let pages = this.transformPages(response.data);
              return this.cachePages(pages,category,type);
            };
            return [];
          }),
          finalize(() => { delete this.activePageRequests[requestKey]; }),
          shareReplay(1)
        );
    this.activePageRequests[requestKey] = request$;
    return request$;
  };
};
